import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface EventLite {
  date?: string;
  showSchedule?: boolean;
}

function fmtDate(iso?: string): string {
  if (!iso) return 'the WICnic';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

function useEventLite() {
  const [evt, setEvt] = useState<EventLite | null>(null);
  useEffect(() => {
    fetch('/api/public/event?year=2026')
      .then(r => (r.ok ? r.json() : null))
      .then(setEvt)
      .catch(() => setEvt(null));
  }, []);
  return evt;
}

export function RsvpConfirmed() {
  const [params] = useSearchParams();
  const ok = params.get('ok') === '1';
  const already = params.get('already') === '1';
  const reason = params.get('reason');

  if (ok && already) return <AlreadyConfirmed />;
  if (ok) return <Confirmed />;

  return <Failed reason={reason} />;
}

function Confirmed() {
  const evt = useEventLite();
  const showSchedule = evt?.showSchedule !== false;
  const dateLabel    = fmtDate(evt?.date);

  return (
    <section className="section">
      <div className="container confirm">
        <div className="confirm__bar" aria-hidden />
        <span className="eyebrow" style={{ color: 'var(--red)' }}>— You're In</span>
        <h1 className="confirm__title">RSVP Confirmed</h1>
        <p className="confirm__lede">
          See you on {dateLabel}.
        </p>
        <p>
          We've got your headcount. If anything changes, just submit the RSVP form again with
          the same email &mdash; it'll replace your existing RSVP.
        </p>
        <div className="confirm__actions">
          <Link to="/" className="btn btn--ghost">Back to home</Link>
          {showSchedule && <Link to="/schedule" className="btn">See the schedule</Link>}
        </div>
        <style>{`
          .confirm { max-width: 640px; }
          .confirm__bar {
            width: 64px;
            height: 6px;
            background: var(--red);
            margin-bottom: 1.5rem;
          }
          .confirm__title { font-size: clamp(2.5rem, 5vw, 4rem); margin-bottom: 1rem; }
          .confirm__lede {
            font-family: var(--font-sub);
            font-weight: 500;
            font-size: 1.4rem;
            color: var(--charcoal);
            margin: 0 0 2rem 0;
          }
          .confirm__actions { display: flex; gap: 1rem; margin-top: 2.5rem; flex-wrap: wrap; }
        `}</style>
      </div>
    </section>
  );
}

function AlreadyConfirmed() {
  return (
    <section className="section">
      <div className="container confirm">
        <span className="eyebrow">— Already Confirmed</span>
        <h1>You're already on the list</h1>
        <p className="confirm__lede">No action needed &mdash; we have your RSVP.</p>
        <div className="confirm__actions">
          <Link to="/" className="btn btn--ghost">Back to home</Link>
          <Link to="/rsvp" className="btn">Update my RSVP</Link>
        </div>
        <style>{`
          .confirm { max-width: 640px; }
          .confirm__lede { font-family: var(--font-sub); font-weight: 500; font-size: 1.2rem; color: var(--charcoal); }
          .confirm__actions { display: flex; gap: 1rem; margin-top: 2rem; flex-wrap: wrap; }
        `}</style>
      </div>
    </section>
  );
}

function Failed({ reason }: { reason: string | null }) {
  const message =
    reason === 'invalid-or-expired' ? 'This link is no longer valid. Magic links expire after 24 hours.'
    : reason === 'not-found'        ? "We couldn't find an RSVP for this link. It may have been deleted."
    : reason === 'missing-token'    ? 'This link is missing its verification token.'
    :                                 'Something went wrong verifying your link.';

  return (
    <section className="section">
      <div className="container confirm">
        <span className="eyebrow" style={{ color: 'var(--charcoal)' }}>— Verification Failed</span>
        <h1>We couldn't confirm that link</h1>
        <p className="confirm__lede">{message}</p>
        <p>Just submit the RSVP form again to get a fresh confirmation email.</p>
        <div className="confirm__actions">
          <Link to="/rsvp" className="btn">Try again</Link>
          <Link to="/" className="btn btn--ghost">Home</Link>
        </div>
        <style>{`
          .confirm { max-width: 640px; }
          .confirm__lede { font-family: var(--font-sub); font-weight: 500; font-size: 1.2rem; color: var(--charcoal); }
          .confirm__actions { display: flex; gap: 1rem; margin-top: 2rem; flex-wrap: wrap; }
        `}</style>
      </div>
    </section>
  );
}
